import React, { useCallback, useEffect, useRef } from 'react';
import { parsePhoneNumberWithError } from 'libphonenumber-js';

import { Dropdown } from '../Dropdown';
import { Input } from '../Input';
import { CountryCodeWrapper, FlagCircle, Wrapper } from './PhoneInput.styles';
import type { PhoneInputProps } from './PhoneInput.types';

const MAX_PHONE_DIGITS = 15;

const AUTOCOMPLETE_INPUT_TYPES = new Set(['insertReplacementText', 'insertFromPaste']);

function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 4) return digits;
  if (digits.length <= 7) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`;
}

function stripNonDigits(value: string): string {
  return value.replace(/\D/g, '');
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  countryCodes,
  disabled,
  id,
  name,
  placeholder,
  error,
  required,
  bare,
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  searchPlaceholder,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const selectedCountry = countryCodes.find((c) => c.code === value.countryCode) || countryCodes[0];

  const dropdownOptions = countryCodes.map((c) => ({
    value: c.code,
    label: `${c.flag || ''} ${c.dialCode} ${c.label}`.trim(),
  }));

  const handleCountryChange = (code: string) => {
    onChange({ ...value, countryCode: code });
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = stripNonDigits(e.target.value);
    if (digits.length <= MAX_PHONE_DIGITS) {
      onChange({ ...value, number: digits });
    }
  };

  const handleNativeInput = useCallback(
    (e: Event) => {
      const inputEvent = e as InputEvent;
      if (!AUTOCOMPLETE_INPUT_TYPES.has(inputEvent.inputType || '')) return;

      const target = e.target as HTMLInputElement;
      const rawValue = target.value;
      const digits = stripNonDigits(rawValue);

      if (!digits) return;

      e.stopImmediatePropagation();

      try {
        const parsed = parsePhoneNumberWithError('+' + digits);
        if (parsed && parsed.country) {
          const matchedCountry = countryCodes.find((c) => c.code === parsed.country!.toLowerCase());
          const newCountryCode = matchedCountry ? matchedCountry.code : value.countryCode;
          const nationalNumber = parsed.nationalNumber.toString();
          onChange({
            number: nationalNumber.slice(0, MAX_PHONE_DIGITS),
            countryCode: newCountryCode,
          });
          return;
        }
      } catch {
        // Could not parse as international — use raw digits
      }

      onChange({ ...value, number: digits.slice(0, MAX_PHONE_DIGITS) });
    },
    [countryCodes, onChange, value]
  );

  useEffect(() => {
    const input = inputRef.current;
    if (!input) return;
    input.addEventListener('input', handleNativeInput, true);
    return () => input.removeEventListener('input', handleNativeInput, true);
  }, [handleNativeInput]);

  return (
    <Wrapper $error={error} $bare={bare}>
      <CountryCodeWrapper $bare={bare}>
        <Dropdown
          options={dropdownOptions}
          value={value.countryCode}
          onChange={handleCountryChange}
          disabled={disabled}
          aria-label="Country code"
          align="left"
          withSearch
          searchPlaceholder={searchPlaceholder}
          renderValue={() => <FlagCircle>{selectedCountry?.flag}</FlagCircle>}
        />
      </CountryCodeWrapper>

      <Input
        ref={inputRef}
        variant="borderless"
        type="tel"
        id={id}
        name={name}
        value={formatPhoneNumber(value.number)}
        onChange={handleNumberChange}
        disabled={disabled}
        placeholder={placeholder}
        required={required}
        aria-label={ariaLabel}
        aria-describedby={ariaDescribedBy}
        error={error}
      />
    </Wrapper>
  );
};
